import { useId, useState } from 'react';
import { T, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { toast } from 'src/components/ui/toast';
import { useCreateNamespace } from 'src/api/rpc/hooks/namespaces/useNamespaces';
import { isValidNamespaceKey } from './namespaceLabels';
import {
  emptyNamespaceDraft,
  withNamespaceKey,
  withNamespaceName,
} from './namespaceDraft';

/** Names a new namespace and creates it; the Key follows the Name until typed. */
export function CreateNamespaceDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (namespaceId: string) => void;
}) {
  const gt = useGT();
  const idPrefix = useId();
  const createNamespace = useCreateNamespace();
  const [draft, setDraft] = useState(emptyNamespaceDraft);

  function resetAndClose() {
    setDraft(emptyNamespaceDraft);
    onOpenChange(false);
  }

  async function handleCreate() {
    const name = draft.name.trim();
    const key = draft.key.trim().toUpperCase();
    if (!name || !isValidNamespaceKey(key)) return;
    try {
      const created = await createNamespace.mutateAsync({ name, key });
      onCreated(created.id);
      resetAndClose();
      toast.success(
        gt('Namespace created', {
          context: 'toast after creating a namespace from a picker',
        }),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Failed to create the namespace', {
              context: 'toast after creating a namespace failed',
            }),
      );
    }
  }

  const canCreate =
    draft.name.trim().length > 0 &&
    isValidNamespaceKey(draft.key) &&
    !createNamespace.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) resetAndClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title, create a namespace from a picker">
              New namespace
            </T>
          </DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            // Portaled, but React still bubbles a submit to a form around the picker.
            event.stopPropagation();
            if (canCreate) void handleCreate();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-namespace-name`}>
              <T context="namespace form field label">Name</T>
            </Label>
            <p className="text-xs leading-relaxed text-muted-foreground">
              <T context="hint under the namespace name field">
                Human-readable label shown in pickers and the inbox filter.
              </T>
            </p>
            <Input
              id={`${idPrefix}-namespace-name`}
              value={draft.name}
              onChange={(e) =>
                setDraft((current) =>
                  withNamespaceName(current, e.target.value),
                )
              }
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-namespace-key`}>
              <T context="namespace form field label">Key</T>
            </Label>
            <p className="text-xs leading-relaxed text-muted-foreground">
              <T context="hint under the namespace key field">
                Short uppercase prefix used in task ids (e.g. SHIP-42). Up to 10
                letters or digits.
              </T>
            </p>
            <Input
              id={`${idPrefix}-namespace-key`}
              className="font-mono uppercase"
              value={draft.key}
              maxLength={10}
              onChange={(e) =>
                setDraft((current) => withNamespaceKey(current, e.target.value))
              }
              placeholder={gt('SHIP', {
                context: 'example namespace key placeholder',
              })}
            />
            {draft.key.length > 0 && !isValidNamespaceKey(draft.key) && (
              <p className="text-[11px] text-destructive">
                <T context="namespace key validation error">
                  Use 1-10 letters or digits (e.g. &quot;SHIP&quot;).
                </T>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={resetAndClose}>
              <T context="button, cancel creating a namespace">Cancel</T>
            </Button>
            <Button type="submit" disabled={!canCreate}>
              {createNamespace.isPending ? (
                <T context="button label while creating a namespace">
                  Creating…
                </T>
              ) : (
                <T context="button, create the namespace">Create</T>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
